import React, { useCallback } from 'react';
import classNames from 'classnames';
import Lottie from 'lottie-react';

import { useAppSelector } from 'store/hooks';
import {
  DiscordConfig,
  Gradient,
  NFTCollection,
  Role,
  TwitterUser,
  Weight,
} from 'api/raffle/raffle_detail';
import {
  CheckContainedFill,
  Circle,
  Loader03,
  XCircleContainedFill,
} from 'lib/lucky-x-icons-2';
import multiplierIcon from '@/assets/emoji/multiplier.png';
import hourglassIcon from 'assets/icons/hourglassIcon.json';

interface IProps {
  verifyStatus: any;
  icon: string;
  itemDiscord?: DiscordConfig;
}

const TaskItem: React.FC<IProps> = ({ verifyStatus, icon, itemDiscord }) => {
  const {
    raffleInfo: { requirements },
  } = useAppSelector((state) => state.raffleDetails);

  const taskConfig = useCallback(
    (typeValue: string) => {
      return requirements?.find((item) => item.type === typeValue);
    },
    [requirements]
  );

  const multiplierIconImg = useCallback(() => {
    return <img src={multiplierIcon.src} className="w-5" alt="" />;
  }, []);

  const andTwitterMarkRender = useCallback(
    (list: TwitterUser[], item: string) => {
      if (list.length === 1) {
        return ' ';
      } else if (list.length === 2) {
        return <span>{list[0]?.screen_name === item ? ' and ' : ' '}</span>;
      } else {
        return (
          <span>
            {list[list.length - 1]?.screen_name === item
              ? ' '
              : list[list.length - 2]?.screen_name === item
              ? ' and '
              : ' , '}
          </span>
        );
      }
    },
    []
  );

  const verifyIcon = useCallback((verifyStatus: string) => {
    return (
      <div>
        {verifyStatus === 'loading' && (
          <Loader03 size={24} color="#fff" className="animate-spin" />
        )}
        {verifyStatus === 'failed' && (
          <XCircleContainedFill size={24} className="text-warn-300" />
        )}
        {verifyStatus === 'success' && (
          <CheckContainedFill size={24} color="#98FF59" />
        )}
        {verifyStatus === 'limit' && (
          <Lottie animationData={hourglassIcon} style={{ width: 16 }} />
        )}
      </div>
    );
  }, []);

  const iconRender = useCallback(() => {
    if (!!verifyStatus) {
      return verifyIcon(verifyStatus);
    } else {
      return <Circle size={24} className="text-neutral-400" />;
    }
  }, [verifyIcon, verifyStatus]);

  const nftMultiplierRender = useCallback((weight: Weight) => {
    if (weight?.type === 'PER_NFT') {
      return (
        <div className="flex items-center gap-1 font-medium">
          Per NFT x {weight?.multiplier} {multiplierIconImg()} .
        </div>
      );
    } else {
      return (
        <div className="font-medium">
          {weight?.non_holder && (
            <div className="flex items-center gap-1">
              Non-holder x 1 {multiplierIconImg()} ;
            </div>
          )}
          {!!weight?.default_multiplier ? (
            <div className="flex items-center gap-1">
              Hold NFT x {weight?.default_multiplier} {multiplierIconImg()} ;
            </div>
          ) : (
            <div className="flex items-center gap-1">
              Hold NFT x 1 {multiplierIconImg()} ;
            </div>
          )}
          {weight?.gradients?.map((item: Gradient, index: number) => (
            <div key={index} className="flex">
              <span className="flex items-center gap-1">
                Hold at least {item?.threshold} NFTs x{item?.multiplier}{' '}
                {multiplierIconImg()}
              </span>
              {index !== (weight?.gradients as Gradient[])?.length - 1
                ? ' ;'
                : ' .'}
            </div>
          ))}
        </div>
      );
    }
  }, []);

  const textRender = useCallback(() => {
    if (icon === 'walletBalance') {
      return (
        <div>
          Have at least{' '}
          <span className="font-medium ">
            {taskConfig('WALLET_BALANCE')?.config?.eth}
          </span>{' '}
          ETH in your wallet.
        </div>
      );
    } else if (icon === 'walletHodler') {
      return (
        <div>
          {!!(
            taskConfig('WALLET_NFT')?.config?.nft_collections as NFTCollection[]
          )[0]?.weight?.type
            ? 'Verify the holder of'
            : 'Hold at least one'}{' '}
          {taskConfig('WALLET_NFT')?.config?.nft_collections?.length !== 0 &&
            (
              taskConfig('WALLET_NFT')?.config
                ?.nft_collections as NFTCollection[]
            )?.map((item: NFTCollection, index: number) => (
              <span key={index}>
                <a href={item?.link} target="_blank" rel="noreferrer">
                  <span className="font-medium underline">
                    {`${item?.name}`}{' '}
                  </span>
                </a>
                {(
                  taskConfig('WALLET_NFT')?.config
                    ?.nft_collections as NFTCollection[]
                )?.length -
                  1 !==
                  index && ' , '}
              </span>
            ))}{' '}
          {!(
            taskConfig('WALLET_NFT')?.config?.nft_collections as NFTCollection[]
          )[0]?.weight?.type && 'in your wallet'}
          {taskConfig('WALLET_NFT')?.config?.nft_collections?.length === 1 &&
          !!(
            taskConfig('WALLET_NFT')?.config?.nft_collections as NFTCollection[]
          )[0]?.weight?.type
            ? nftMultiplierRender(
                (
                  taskConfig('WALLET_NFT')?.config
                    ?.nft_collections as NFTCollection[]
                )[0]?.weight
              )
            : '.'}
        </div>
      );
    } else if (icon === 'walletAllowList') {
      return (
        <div>
          Users must be in the{' '}
          <span className="font-medium ">
            {taskConfig('WALLET_ALLOW_LIST')?.config?.name}
          </span>{' '}
          list.
        </div>
      );
    } else if (icon === 'joinDiscord') {
      return (
        <div>
          Join{' '}
          <span className="font-medium underline">
            <span
              onClick={() => {
                if (!!itemDiscord?.guild?.invite_link) {
                  open(
                    itemDiscord?.guild?.invite_link.includes('http')
                      ? itemDiscord?.guild?.invite_link
                      : 'https://'.concat(
                          itemDiscord?.guild?.invite_link as string
                        )
                  );
                }
              }}
              className={classNames({
                'cursor-pointer': !!itemDiscord?.guild?.invite_link,
              })}
            >
              {itemDiscord?.guild?.server_name}
            </span>
          </span>{' '}
          Discord
          {itemDiscord?.roles?.length !== 0 && (
            <span>
              {' '}
              and have{' '}
              {(itemDiscord?.roles as Role[])?.length > 1 &&
                'at least one role of'}{' '}
              {(itemDiscord?.roles as Role[])
                .filter((itemI) => !!itemI.name)
                .map((itemRole: Role, index: number) => {
                  if (
                    itemDiscord?.roles?.filter(
                      (itemF) => Number(itemF.multiplier) > 1
                    ).length === 0
                  ) {
                    return (
                      <span key={index}>
                        <span className="break-all font-medium underline">
                          {itemRole?.name}{' '}
                          {itemRole?.is_stacking && <>(Stacked)</>}
                        </span>
                        {(itemDiscord?.roles as Role[])?.length === 1 && (
                          <>&nbsp;role</>
                        )}
                        {index !==
                        (itemDiscord?.roles as Role[]).filter(
                          (itemI) => !!itemI.name
                        ).length -
                          1
                          ? ' , '
                          : '. '}
                      </span>
                    );
                  } else {
                    return (
                      <span key={index} className="flex flex-wrap">
                        <span className="flex flex-wrap items-center gap-1 break-all font-medium underline">
                          {itemRole?.name} x {itemRole?.multiplier}{' '}
                          {multiplierIconImg()}{' '}
                          {itemRole?.is_stacking && <>(Stacked)</>}
                          {(itemDiscord?.roles as Role[])?.length === 1 && (
                            <>&nbsp;role</>
                          )}
                          {index !==
                          (itemDiscord?.roles as Role[]).filter(
                            (itemI) => !!itemI.name
                          ).length -
                            1
                            ? ' , '
                            : '. '}
                        </span>
                      </span>
                    );
                  }
                })}
            </span>
          )}
        </div>
      );
    } else if (icon === 'followTwitter') {
      return (
        <div>
          Follow{' '}
          {taskConfig('TWITTER')?.config?.follow_users?.length !== 0 &&
            (taskConfig('TWITTER')?.config?.follow_users as TwitterUser[]).map(
              (item: TwitterUser, index: number) => (
                <span key={index}>
                  <a
                    href={`https://twitter.com/${item?.screen_name}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="font-medium underline">
                      {`@${item?.screen_name}`}{' '}
                    </span>
                  </a>
                  {andTwitterMarkRender(
                    taskConfig('TWITTER')?.config
                      ?.follow_users as TwitterUser[],
                    item?.screen_name
                  )}
                </span>
              )
            )}{' '}
          on Twitter.
        </div>
      );
    } else if (icon === 'likeRetweet') {
      return (
        <div>
          Like & Retweet{' '}
          <a
            href={taskConfig('TWITTER')?.config?.retweet_and_like}
            target="_blank"
            rel="noreferrer"
          >
            <span className="font-medium underline">this tweet</span>
          </a>
          (Delay 5s) .
        </div>
      );
    }
  }, [
    andTwitterMarkRender,
    icon,
    itemDiscord?.guild?.invite_link,
    itemDiscord?.guild?.server_name,
    itemDiscord?.roles,
    multiplierIconImg,
    nftMultiplierRender,
    taskConfig,
  ]);

  return (
    <div
      className={classNames(
        'mt-2 flex items-center gap-3 rounded-2xl border-[1px]  p-4',
        {
          'border-warn-300 bg-warn-900': verifyStatus === 'failed',
          'border-neutral-600': verifyStatus !== 'failed',
        }
      )}
    >
      <div className="shrink-0">{iconRender()}</div>
      <div className="w-full whitespace-pre-line text-base  font-normal text-neutral-100 md:w-auto">
        {textRender()}
      </div>
    </div>
  );
};

export default TaskItem;
